import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@mui/material';
import {
  DotsSixVertical,
  PaintBrush,
  Plus,
  Trash
} from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import {
  INode,
  ITypeNode,
  useFormBuilder,
} from '../../Context/FormBuilderContext';
import { ActionTypes } from '../../Reducer';
import { ListFormData } from '../../schemas/list.schema';
import { listFormToNode, listNodeToForm } from '../../utils/nodeDataTransformers';
import { CustomizationForm } from '../FormCustomization/CustomizationForm';

interface DataInputNode {
  id: string;
  label: string;
  type: string;
}

interface ListSettingsProps {
  node: INode;
}

export function ListSettings({ node }: ListSettingsProps) {
  const { state, dispatch } = useFormBuilder();
  const [expandedNodeId, setExpandedNodeId] = useState<string | null>(null);
  const [editingItemIndex, setEditingItemIndex] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  const formsMethods = useForm<ListFormData>({
    defaultValues: listNodeToForm(node),
  });

  useEffect(() => {
    formsMethods.reset(listNodeToForm(node));
  }, [node.id, node.data, formsMethods]);

  const { fields, append, remove, move } = useFieldArray({
    control: formsMethods.control,
    name: 'listItems',
  });

  const formValues = formsMethods.watch();

  const handleNodeSelect = (node: DataInputNode) => {
    setExpandedNodeId(expandedNodeId === node.id ? null : node.id);
    formsMethods.setValue('varDataInputId', node.id);
  };

  const selectedNodeData = state.varInputNodes.find(
    node => node.id === formValues.varDataInputId,
  );

  const handleRemoveItem = (index: number) => {
    remove(index);
  };

  const handleAddItem = () => {
    append({ description: '' });
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    if (result.destination.index === result.source.index) {
      return;
    }

    move(result.source.index, result.destination.index);
  };

  useEffect(() => {
    const subscription = formsMethods.watch((data) => {
      if (data && Object.keys(data).length > 0) {
        const updatedData = listFormToNode(data as ListFormData, node);
        dispatch({
          type: ActionTypes.UPDATE_NODES_DATA,
          payload: {
            id: node.id,
            updates: { data: updatedData },
          },
        });
      }
    });

    return () => subscription.unsubscribe();
  }, [formsMethods.watch, node.id, dispatch]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '16px 16px 0 16px',
        }}
      >
        <PaintBrush size={16} color="#101828" />
        <Typography
          style={{ fontSize: '14px', fontWeight: '500', color: '#101828' }}
        >
          List Settings
        </Typography>
      </Box>

      <FormProvider {...formsMethods}>
        <form style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Box
            sx={{
              display: 'flex',
              gap: '4px',
              padding: '0px 16px 0 16px',
            }}
          >
            <Box
              onClick={() => setActiveTab(0)}
              sx={{
                px: 2,
                py: 1,
                cursor: 'pointer',
                color: activeTab === 0 ? '#4F46E5' : '#6B7280',
                backgroundColor: activeTab === 0 ? '#F3F4F6' : 'transparent',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: activeTab === 0 ? '#F3F4F6' : '#F9FAFB',
                },
              }}
            >
              Content
            </Box>

            <Box
              onClick={() => setActiveTab(1)}
              sx={{
                px: 2,
                py: 1,
                cursor: 'pointer',
                color: activeTab === 1 ? '#4F46E5' : '#6B7280',
                backgroundColor: activeTab === 1 ? '#F3F4F6' : 'transparent',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: activeTab === 1 ? '#F3F4F6' : '#F9FAFB',
                },
              }}
            >
              Design
            </Box>
          </Box>

          <div
            style={{ width: '100%', height: '1px', backgroundColor: '#EAECF0' }}
          />

          {activeTab === 0 && (
            <Box sx={{ padding: '0px 16px 0 16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Controller
            name="title"
            control={formsMethods.control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Title"
                placeholder="Enter title..."
                fullWidth
                variant="outlined"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                size="small"
                sx={{
                  '& .MuiInputBase-root': {
                    margin: '0px',
                    fontSize: '14px',
                    height: '38px',
                  },
                }}
              />
            )}
          />

          <Controller
            name="question"
            control={formsMethods.control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Question"
                placeholder="Enter your question..."
                fullWidth
                variant="outlined"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                size="small"
                sx={{
                  '& .MuiInputBase-root': {
                    margin: '0px',
                    fontSize: '14px',
                    height: '38px',
                  },
                }}
              />
            )}
          />

          <Typography
            style={{
              fontSize: '12px',
              fontWeight: '400',
              color: '#475467',
            }}
          >
            Please select which "Data input" to use for this list field
          </Typography>

          {selectedNodeData && (
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
            >
              <Typography
                style={{
                  fontSize: '12px',
                  fontWeight: '400',
                  color: '#475467',
                }}
              >
                Node selected:
              </Typography>

              <div
                style={{
                  backgroundColor: '#F9FAFB',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #E4E7EC',
                }}
              >
                <Typography
                  variant="body2"
                  style={{ fontWeight: '500', marginBottom: '4px' }}
                >
                  {selectedNodeData.label}
                </Typography>
                <Typography variant="caption" style={{ color: '#475467' }}>
                  Type: {formValues?.typeQuestion} | Required:{' '}
                  {formValues?.requiredField ? 'Yes' : 'No'}
                </Typography>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Typography
              style={{
                fontSize: '12px',
                fontWeight: '400',
                color: '#475467',
              }}
            >
              Select data input
            </Typography>

            {state.varInputNodes.map(node => (
              <div
                key={node.id}
                style={{
                  border: '1px solid #E4E7EC',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  transition: 'all 0.2s ease',
                }}
              >
                <div
                  onClick={() => handleNodeSelect(node)}
                  style={{
                    padding: '12px',
                    cursor: 'pointer',
                    backgroundColor:
                      expandedNodeId === node.id ? '#F0F4FF' : 'transparent',
                    borderBottom:
                      expandedNodeId === node.id ? '1px solid #E4E7EC' : 'none',
                  }}
                  onMouseEnter={e => {
                    if (expandedNodeId !== node.id) {
                      e.currentTarget.style.backgroundColor = '#F9FAFB';
                      e.currentTarget.style.borderColor = '#3650DE';
                    }
                  }}
                  onMouseLeave={e => {
                    if (expandedNodeId !== node.id) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.borderColor = '#E4E7EC';
                    }
                  }}
                >
                  <Typography variant="subtitle2" style={{ fontWeight: '500' }}>
                    Input: {node.label}
                  </Typography>
                </div>

                {expandedNodeId === node.id && (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      padding: '12px',
                      backgroundColor: '#FAFBFC',
                      gap: '8px',
                    }}
                  >
                    <FormControl component="fieldset">
                      <Controller
                        name="requiredField"
                        control={formsMethods.control}
                        render={({ field }) => (
                          <RadioGroup
                            value={field.value ? 'true' : 'false'}
                            onChange={e =>
                              field.onChange(e.target.value === 'true')
                            }
                            style={{ flexDirection: 'row' }}
                          >
                            <FormControlLabel
                              value="true"
                              control={<Radio size="small" />}
                              label="Required"
                            />
                            <FormControlLabel
                              value="false"
                              control={<Radio size="small" />}
                              label="Optional"
                            />
                          </RadioGroup>
                        )}
                      />
                    </FormControl>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}
          >
            <Typography
              style={{
                fontSize: '12px',
                fontWeight: '400',
                color: '#475467',
              }}
            >
              List type:
            </Typography>

            <Controller
              name="typeQuestion"
              control={formsMethods.control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  sx={{
                    '& .MuiInputBase-root': {
                      height: '32px',
                      borderRadius: '4px',
                      border: '1px solid #EAECF0',
                      backgroundColor: '#F9FAFB',
                      color: '#475467',
                      margin: '0px',
                    },
                  }}
                  fullWidth
                  variant="outlined"
                  size="small"
                >
                  <MenuItem value="dropdown">Dropdown</MenuItem>
                  <MenuItem value="single">Single Choice</MenuItem>
                  <MenuItem value="multiple">Multiple Choice</MenuItem>
                </TextField>
              )}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                style={{
                  fontSize: '12px',
                  fontWeight: '400',
                  color: '#475467',
                }}
              >
                List items:
              </Typography>

              <Button
                variant="text"
                onClick={handleAddItem}
                size="small"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '12px',
                  fontWeight: '400',
                  color: '#475467',
                  width: 'fit-content',
                  paddingLeft: '4px',
                  paddingRight: '4px',
                  borderRadius: '4px',
                  gap: '4px',
                }}
              >
                <Plus size={12} style={{ marginTop: '-4px' }} />
                Add Item
              </Button>
            </div>
                
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="list-items">
                {(provided) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
                  >
                    {fields.map((field, index) => (
                      <Draggable
                        key={field.id}
                        draggableId={field.id}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            style={{
                              ...provided.draggableProps.style,
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              backgroundColor: snapshot.isDragging ? '#f0f0f0' : 'transparent',
                              borderRadius: snapshot.isDragging ? '8px' : '0',
                              padding: snapshot.isDragging ? '4px' : '0',
                              opacity: snapshot.isDragging ? 0.8 : 1,
                            }}
                          >
                            <div
                              {...provided.dragHandleProps}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'grab',
                                padding: '4px',
                                color: '#666',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.color = '#3650DE';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.color = '#666';
                              }}
                            >
                              <DotsSixVertical size={20} />
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                flex: 1,
                              }}
                            >
                              <div style={{ flex: 1 }}>
                                {editingItemIndex === index ? (
                                  <Controller
                                    name={`listItems.${index}.description`}
                                    control={formsMethods.control}
                                    render={({ field, fieldState }) => (
                                      <TextField
                                        {...field}
                                        placeholder={`Item ${index + 1}`}
                                        size="small"
                                        fullWidth
                                        variant="outlined"
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        onBlur={() => setEditingItemIndex(null)}
                                        autoFocus
                                      />
                                    )}
                                  />
                                ) : (
                                  <Typography
                                    variant="body2"
                                    onClick={() => setEditingItemIndex(index)}
                                    style={{
                                      cursor: 'pointer',
                                      minHeight: '32px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      paddingTop: '4px',
                                      paddingBottom: '4px',
                                      paddingLeft: '8px',
                                      paddingRight: '8px',
                                      whiteSpace: 'pre-wrap',
                                    }}
                                    onMouseEnter={e => {
                                      e.currentTarget.style.backgroundColor = '#f5f5f5';
                                      e.currentTarget.style.borderRadius = '4px';
                                    }}
                                    onMouseLeave={e => {
                                      e.currentTarget.style.backgroundColor = 'transparent';
                                    }}
                                  >
                                    {formsMethods.watch(`listItems.${index}.description`) ||
                                      `Click to edit item ${index + 1}`}
                                  </Typography>
                                )}
                              </div>
                            </div>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => handleRemoveItem(index)}
                              style={{
                                minWidth: 'auto',
                                padding: '8px',
                                color: '#EF5350',
                              }}
                            >
                              <Trash size={16} />
                            </Button>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
            </Box>
          )}

          {activeTab === 1 && (
            <Box sx={{ padding: '0px 16px 0 16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <CustomizationForm
            control={formsMethods.control}
            type={ITypeNode.LIST}
          />
            </Box>
          )}
        </form>
      </FormProvider>
    </>
  );
}
